import type {
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  Router,
  RouteRecordRaw,
} from "vue-router"
import type { OrganizationScope } from "@/types/organization-scope"
import { isNavigationFailure, NavigationFailureType } from "vue-router"
import { isOrganizationSelectPath } from "@/router/hqAccess"

export type BusinessContextNavigationStore = {
  canAccessRoute: (route: RouteLocationNormalizedLoaded) => boolean
  hasRouteName: (name: string | symbol) => boolean
  getFirstAccessibleRoute: () => RouteRecordRaw | undefined
}

export type BusinessContextNavigationRouter = Pick<
  Router,
  "currentRoute" | "replace" | "resolve"
>

export type PostSwitchNavigationResult = {
  redirected: boolean
  fallbackUsed: boolean
}

type ViewSystem = "hq" | "shop" | "unknown"

const NO_POST_SWITCH_NAVIGATION: PostSwitchNavigationResult = {
  redirected: false,
  fallbackUsed: false,
}

export function resolveScopeById(
  store: {
    activeScopeId: string
    activeScope?: OrganizationScope
    organizations?: OrganizationScope[]
  },
  scopeId: string,
) {
  if (store.activeScopeId === scopeId && store.activeScope) return store.activeScope
  return store.organizations?.find(item => item.id === scopeId)
}

function resolveViewSystem(scope: OrganizationScope | undefined, scopeId: string): ViewSystem {
  if (scope?.type === "chain-headquarter" || scopeId.startsWith("chain-headquarter-")) return "hq"
  if (scope?.type === "chain-region" || scopeId.startsWith("chain-region-")) return "hq"
  if (
    scope?.type === "shop"
    || scope?.type === "chain-store"
    || scopeId.startsWith("shop-")
    || scopeId.startsWith("chain-store-")
  ) {
    return "shop"
  }
  return "unknown"
}

export function isCrossViewSystemSwitch(
  previousScope: OrganizationScope | undefined,
  previousScopeId: string,
  nextScope: OrganizationScope | undefined,
  nextScopeId: string,
) {
  const previousSystem = resolveViewSystem(previousScope, previousScopeId)
  const nextSystem = resolveViewSystem(nextScope, nextScopeId)
  return previousSystem !== "unknown"
    && nextSystem !== "unknown"
    && previousSystem !== nextSystem
}

function isInternalLandingPath(path: string) {
  const normalizedPath = path.split(/[?#]/, 1)[0]
  return isOrganizationSelectPath(normalizedPath)
    || normalizedPath === "/no-organization"
    || normalizedPath === "/403"
    || normalizedPath === "/404"
    || normalizedPath === "/login"
    || normalizedPath === "/wx_login"
}

function isCatchAllRouteRecord(route: { name?: string | symbol | null; path?: string }) {
  return route.name === "ErrorPage" || Boolean(route.path?.includes(":pathMatch"))
}

function isAccessibleBusinessLanding(
  route: RouteLocationNormalizedLoaded,
  permissionStore: BusinessContextNavigationStore,
  options: { trustRouteRegistration?: boolean } = {},
) {
  if (isInternalLandingPath(route.path)) return false
  const leafRoute = route.matched.at(-1)
  if (!leafRoute?.name || isCatchAllRouteRecord(leafRoute)) return false
  const isRegisteredBusinessRoute = options.trustRouteRegistration
    ? true
    : permissionStore.hasRouteName(leafRoute.name)
  return isRegisteredBusinessRoute && permissionStore.canAccessRoute(route)
}

function isRequestedRouteAccessible(
  router: BusinessContextNavigationRouter,
  permissionStore: BusinessContextNavigationStore,
  targetRoute: RouteLocationRaw,
) {
  try {
    return isAccessibleBusinessLanding(
      router.resolve(targetRoute) as RouteLocationNormalizedLoaded,
      permissionStore,
    )
  } catch {
    return false
  }
}

async function replaceAndConfirmLanding(
  router: BusinessContextNavigationRouter,
  permissionStore: BusinessContextNavigationStore,
  targetRoute: RouteLocationRaw,
  options: { acceptForbidden?: boolean; trustRouteRegistration?: boolean } = {},
) {
  let navigationFailure: unknown
  try {
    navigationFailure = await router.replace(targetRoute)
  } catch {
    return false
  }
  if (
    isNavigationFailure(navigationFailure)
    && !isNavigationFailure(navigationFailure, NavigationFailureType.duplicated)
  ) {
    return false
  }
  const finalRoute = router.currentRoute.value
  if (finalRoute.path === "/403") return Boolean(options.acceptForbidden)
  return isAccessibleBusinessLanding(finalRoute, permissionStore, options)
}

async function redirectToViewDefault(
  router: BusinessContextNavigationRouter,
  permissionStore: BusinessContextNavigationStore,
  defaultRoute?: RouteLocationRaw,
): Promise<PostSwitchNavigationResult> {
  if (
    defaultRoute
    && isRequestedRouteAccessible(router, permissionStore, defaultRoute)
  ) {
    const defaultEntered = await replaceAndConfirmLanding(
      router,
      permissionStore,
      defaultRoute,
    )
    if (defaultEntered) {
      return { redirected: true, fallbackUsed: true }
    }
  }

  const firstRoute = permissionStore.getFirstAccessibleRoute()
  if (firstRoute?.name) {
    const defaultEntered = await replaceAndConfirmLanding(
      router,
      permissionStore,
      { name: firstRoute.name },
      { trustRouteRegistration: true },
    )
    if (defaultEntered) {
      return { redirected: true, fallbackUsed: true }
    }
  }

  const forbiddenEntered = await replaceAndConfirmLanding(
    router,
    permissionStore,
    "/403",
    { acceptForbidden: true },
  )
  if (!forbiddenEntered) {
    throw new Error("机构切换成功，但无法进入当前视图的可用页面")
  }
  return { redirected: true, fallbackUsed: true }
}

export async function navigateAfterBusinessContextSwitch(options: {
  route: RouteLocationNormalizedLoaded
  router: BusinessContextNavigationRouter
  permissionStore: BusinessContextNavigationStore
  targetRoute?: RouteLocationRaw
  defaultRoute?: RouteLocationRaw
  crossedViewSystem: boolean
}): Promise<PostSwitchNavigationResult> {
  if (options.targetRoute) {
    if (isRequestedRouteAccessible(options.router, options.permissionStore, options.targetRoute)) {
      const targetEntered = await replaceAndConfirmLanding(
        options.router,
        options.permissionStore,
        options.targetRoute,
      )
      if (targetEntered) {
        return { redirected: true, fallbackUsed: false }
      }
    }
    return redirectToViewDefault(
      options.router,
      options.permissionStore,
      options.defaultRoute,
    )
  }

  if (
    !options.crossedViewSystem
    && isAccessibleBusinessLanding(options.route, options.permissionStore)
  ) {
    return NO_POST_SWITCH_NAVIGATION
  }

  return redirectToViewDefault(
    options.router,
    options.permissionStore,
    options.defaultRoute,
  )
}
